/**
 * Black Friday 2025 - iCart Progress Bar
 *
 * Displays a progress bar in the iCart showing free gift unlock progress.
 * Depends on: window.BF2025CartProgress API from black-friday-2025-cart-manager.js
 * Depends on: window.BF_PROGRESS_BAR_HTML template from black-friday-2025.liquid
 */

(function() {
  'use strict';

  // Configuration
  const SELECTORS = {
    icartMain: '.icart-main',
    icartItems: '.icart-main .icart-items',
    icartList: '.icart-main .icart-list',
    progressBar: '.bf-progress-bar'
  };

  const GIFT_NAMES = {
    jibbitz: 'Jibbitz',
    decals: 'Decals',
    socks: 'Socks',
    pack: 'a FREE Pack'
  };

  let observer = null;
  let currentProgressBar = null;
  let lastState = null; // Track last known state to prevent redundant updates

  /**
   * Get the current progress state from cart manager
   */
  function getProgressState() {
    if (!window.BF2025CartProgress || typeof window.BF2025CartProgress.getState !== 'function') {
      console.warn('[BF Progress Bar] Cart manager not initialized');
      return null;
    }
    return window.BF2025CartProgress.getState();
  }

  /**
   * Check if tier 4 feature is enabled
   */
  function isTier4Enabled() {
    return window.BF_TIER4_ENABLED === true;
  }

  /**
   * Generate the progress message based on current state
   */
  function getProgressMessage(state) {
    const tier4Enabled = isTier4Enabled();
    const packCount = state.effectivePackCount || state.productCount || 0;
    const totalUnlocked = (state.hasJibbitz ? 1 : 0) + (state.hasDecals ? 1 : 0) + (state.hasSocks ? 1 : 0) + (tier4Enabled && state.hasFreePack ? 1 : 0);

    if (tier4Enabled && totalUnlocked === 4) {
      return "You've unlocked 4 free gifts!";
    } else if (totalUnlocked === 3) {
      if (tier4Enabled) {
        if (packCount >= 4) {
          return "You've unlocked 3 free gifts! Choose your free pack!";
        }
        return `You've unlocked 3 free gifts! Add 1 more to unlock ${GIFT_NAMES.pack}!`;
      }
      return "You've unlocked 3 free gifts!";
    } else if (totalUnlocked === 2) {
      return `You've unlocked 2 free gifts! Add 1 more for ${GIFT_NAMES.socks}`;
    } else if (totalUnlocked === 1) {
      const nextGift = state.hasJibbitz ? GIFT_NAMES.decals : GIFT_NAMES.jibbitz;
      return `You've unlocked 1 free gift! Add 1 more for ${nextGift}`;
    } else {
      return `Add products to unlock free gifts!`;
    }
  }

  /**
   * Check if state has actually changed
   */
  function hasStateChanged(newState) {
    if (!lastState) return true;

    return lastState.effectivePackCount !== newState.effectivePackCount ||
           lastState.productCount !== newState.productCount ||
           lastState.bundleCount !== newState.bundleCount ||
           lastState.hasJibbitz !== newState.hasJibbitz ||
           lastState.hasDecals !== newState.hasDecals ||
           lastState.hasSocks !== newState.hasSocks ||
           lastState.hasFreePack !== newState.hasFreePack;
  }

  /**
   * Update the progress bar UI based on current cart state
   */
  function updateProgressBar(progressBarElement, state) {
    if (!progressBarElement || !state) return;

    // Check if state has changed to prevent unnecessary DOM updates
    if (!hasStateChanged(state)) {
      return;
    }

    // Update last known state
    lastState = { ...state };

    const tier4Enabled = isTier4Enabled();

    // Update dot states
    const dots = progressBarElement.querySelectorAll('.bf-progress-dot');

    // Hide/show 4th dot based on feature flag
    if (dots.length >= 4) {
      dots[3].style.display = tier4Enabled ? '' : 'none';
    }

    // Use effectivePackCount (bundles count as 3 packs each)
    const packCount = state.effectivePackCount || state.productCount || 0;

    if (dots.length >= 3) {
      // First dot (Jibbitz) - 1+ effective packs
      if (state.hasJibbitz || packCount >= 1) {
        dots[0].classList.add('filled');
      } else {
        dots[0].classList.remove('filled');
      }

      // Second dot (Decals) - 2+ effective packs
      if (state.hasDecals || packCount >= 2) {
        dots[1].classList.add('filled');
      } else {
        dots[1].classList.remove('filled');
      }

      // Third dot (Socks) - 3+ effective packs
      if (state.hasSocks || packCount >= 3) {
        dots[2].classList.add('filled');
      } else {
        dots[2].classList.remove('filled');
      }

      // Fourth dot (Free Pack) - only if tier 4 enabled, 4+ effective packs
      if (tier4Enabled && dots.length >= 4) {
        if (state.hasFreePack || packCount >= 4) {
          dots[3].classList.add('filled');
        } else {
          dots[3].classList.remove('filled');
        }
      }
    }

    // Update line fill
    const line = progressBarElement.querySelector('.bf-progress-line');
    if (line) {
      let fillPercentage = 0;

      if (tier4Enabled) {
        // 4-tier fill percentages (0%, 33%, 66%, 100%)
        if (packCount >= 4) {
          fillPercentage = 100;
        } else if (packCount >= 3) {
          fillPercentage = 66;
        } else if (packCount >= 2) {
          fillPercentage = 33;
        } else if (packCount >= 1) {
          fillPercentage = 16;
        }
      } else {
        // 3-tier fill percentages (original behavior)
        if (packCount >= 3) {
          fillPercentage = 100;
        } else if (packCount >= 2) {
          fillPercentage = 100;
        } else if (packCount >= 1) {
          fillPercentage = 50;
        }
      }
      line.style.setProperty('--fill-percentage', `${fillPercentage}%`);
    }

    // Update message
    const message = progressBarElement.querySelector('.bf-progress-message');
    if (message) {
      const newMessage = getProgressMessage(state);
      if (message.textContent !== newMessage) {
        message.textContent = newMessage;
      }
    }
  }

  /**
   * Create and inject the progress bar into iCart
   */
  function injectProgressBar() {
    const icartList = document.querySelector(SELECTORS.icartList);
    if (!icartList) {
      console.warn('[BF Progress Bar] .icart-list not found');
      return;
    }

    // Check if we already have a progress bar
    const existingBar = icartList.querySelector(SELECTORS.progressBar);
    if (existingBar) {
      currentProgressBar = existingBar;
      return;
    }

    // Check if template HTML is available
    if (!window.BF_PROGRESS_BAR_HTML) {
      console.warn('[BF Progress Bar] Template HTML not found in window.BF_PROGRESS_BAR_HTML');
      return;
    }

    // Create progress bar from template
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = window.BF_PROGRESS_BAR_HTML.trim();
    const progressBar = tempDiv.firstChild;

    if (!progressBar) {
      console.warn('[BF Progress Bar] Failed to create progress bar element');
      return;
    }

    // Add tier4 class if feature flag is enabled
    if (isTier4Enabled()) {
      progressBar.classList.add('bf-progress-bar--tier4');
    }

    // Insert as first child of .icart-list
    icartList.insertBefore(progressBar, icartList.firstChild);
    currentProgressBar = progressBar;

    console.log('[BF Progress Bar] Progress bar injected into iCart');
  }

  /**
   * Remove the progress bar from iCart
   */
  function removeProgressBar() {
    if (currentProgressBar && currentProgressBar.parentNode) {
      currentProgressBar.parentNode.removeChild(currentProgressBar);
      currentProgressBar = null;
      console.log('[BF Progress Bar] Progress bar removed');
    }
  }

  /**
   * Check if cart has content (should show progress bar)
   */
  function shouldShowProgressBar() {
    const icartItems = document.querySelector(SELECTORS.icartItems);
    if (!icartItems) return false;

    // Check if icart-items has any content (excluding whitespace)
    const hasContent = icartItems.textContent.trim().length > 0 ||
                       icartItems.children.length > 0;

    // Also check cart state
    const state = getProgressState();
    if (!state) return hasContent;

    // Only show if there are products or bundles in cart
    return hasContent && (state.productCount > 0 || state.bundleCount > 0);
  }

  /**
   * Main update function - called when cart changes
   */
  function handleCartUpdate() {
    const shouldShow = shouldShowProgressBar();
    const state = getProgressState();

    if (shouldShow && state) {
      // Ensure progress bar exists
      if (!currentProgressBar) {
        injectProgressBar();
      }

      // Update the progress bar
      if (currentProgressBar) {
        updateProgressBar(currentProgressBar, state);
      }
    } else {
      // Remove progress bar if cart is empty
      removeProgressBar();
    }
  }

  /**
   * Wait for an element to appear in the DOM using MutationObserver
   */
  function waitForElement(selector, callback) {
    const element = document.querySelector(selector);
    if (element) {
      callback(element);
      return;
    }

    const observer = new MutationObserver((mutations, obs) => {
      const element = document.querySelector(selector);
      if (element) {
        obs.disconnect();
        callback(element);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Initialize MutationObserver to watch for cart changes
   */
  function initObserver() {
    const icartItems = document.querySelector(SELECTORS.icartItems);
    if (!icartItems) {
      console.log('[BF Progress Bar] .icart-items not found, waiting for it to appear...');
      return false;
    }

    // Create observer to watch ONLY .icart-items (not our progress bar)
    observer = new MutationObserver(() => {
      // React immediately to changes - no timeout needed
      handleCartUpdate();
    });

    // Observe ONLY .icart-items, not .icart-main
    // This prevents our progress bar updates from triggering the observer
    observer.observe(icartItems, {
      childList: true,
      subtree: true,
      characterData: true
    });

    console.log('[BF Progress Bar] MutationObserver initialized on .icart-items');

    // Initial update
    handleCartUpdate();

    return true;
  }

  /**
   * Initialize the progress bar system
   */
  function init() {
    console.log('[BF Progress Bar] Initializing...');

    // Wait for DOM and dependencies
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    // Check for dependencies
    if (!window.BF2025CartProgress) {
      console.warn('[BF Progress Bar] Waiting for cart manager...');
      setTimeout(init, 500);
      return;
    }

    if (!window.BF_PROGRESS_BAR_HTML) {
      console.warn('[BF Progress Bar] Waiting for template HTML...');
      setTimeout(init, 500);
      return;
    }

    // Try to initialize observer
    const success = initObserver();
    if (!success) {
      // Wait for iCart to appear using MutationObserver (reactive, no polling)
      waitForElement(SELECTORS.icartItems, () => {
        console.log('[BF Progress Bar] .icart-items appeared, initializing observer');
        initObserver();
      });
    }
  }

  // Start initialization
  init();

  // Also listen for custom cart update events
  if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    document.addEventListener(window.PUB_SUB_EVENTS.cartUpdate, () => {
      handleCartUpdate();
    });
  }

  // Expose refresh function for manual updates if needed
  window.BF2025ProgressBar = {
    refresh: handleCartUpdate,
    getState: getProgressState
  };

})();
